/** Loose shape of one entry in a product's `options` list (see `src/types/surface.ts`'s
 * `SurfaceStateType.options`, typed as `Record<string, any>` even though the real API response —
 * and this app's own storage of it, see `src/components/skus/slice.ts` — is an array). Only the
 * field this module needs is declared. */
type ProductOptionType = {
    name?: string;
};

/**
 * Returns the names of every option this product declares (e.g. `Size`, `Substrate Color`,
 * `Backside`, `Decoration Technology`) that the given `merchantVariables` selection has not set.
 *
 * Exists because DIS's own "I need more information" signaling is inconsistent — it returns a
 * clear 400 with a `remainingOptions` list for some under-specified requests, but a bare 404 ("no
 * design views") for others that are actually the same underlying problem (confirmed live: DIS
 * resolves `CIM-W6NEPVAG` with `Size`+`Substrate Color` alone as a 404, not a 400, even though
 * `decorationPlacement` genuinely exists once `Backside`+`Decoration Technology` are also
 * supplied) — and for at least `printWhite`, DIS answers with a single, specific value even when
 * an axis it varies by (`Substrate Color`) is left unset, rather than flagging the ambiguity. That
 * silently-picked answer is not something Scene Maker can tell apart from a real, reliable one
 * after the fact.
 *
 * Scene Maker already has each product's own declared option list on hand (fetched for the
 * product name/last-modified display — `src/components/skus/saga.ts`), so this checks completeness
 * itself, before trusting DIS's response, rather than relying solely on DIS's signal.
 */
export function unresolvedProductOptions(
    productOptions: ProductOptionType[] | undefined,
    merchantVariables: Record<string, string | undefined> | undefined,
): string[] {
    const optionNames = (productOptions ?? []).map((option) => option.name).filter((name): name is string => !!name);

    return optionNames.filter((name) => !merchantVariables?.[name]);
}
